/**
 * The "Vagas de campanha" card (platform design §5.1.1, Figma `185:5432`): one fraction
 * ("N de M em uso"), the meter, and a badge only when over the limit.
 * Pure and presentational, like `SlotMeter` — a consuming screen fetches with `useCapacity` and
 * passes the result straight through. `trigger`, when given, is the card head's own request
 * action (`RequestSlots`'s dialog trigger); a caller without `control_plane.write` passes none.
 *
 * `SlotMeter` is reused for the bar with `hidePercentage`: the frame states the fraction once,
 * as this card's own value line, and draws no percentage beside the bar.
 */

import type { ReactNode } from 'react';
import { CalendarRange } from 'lucide-react';

import { Badge } from '../../../ui/badge';
import { Card, CardContent } from '../../../ui/card';
import { getLocale } from '../../../i18n';
import { integer } from '../../../i18n/format';
import type { Capacity } from './api/schemas';
import { cm } from './messages';
import { SlotMeter } from './SlotMeter';

/** "N de M em uso", or the owner's "Aguardando slots" while the organisation has no slot at all
 *  (`null` or `0`): "0 de 0 em uso" says nothing a reader can act on. */
export function slotsValue(capacity: Capacity): string {
  const locale = getLocale();
  if (capacity.campaign_slots === null || capacity.campaign_slots === 0) {
    return cm('stats.awaitingSlots');
  }
  return cm('stats.fraction', {
    inUse: integer(capacity.campaigns_in_use, locale),
    slots: integer(capacity.campaign_slots, locale),
  });
}

export function CapacityStats(props: { capacity: Capacity; trigger?: ReactNode }): ReactNode {
  const { capacity, trigger } = props;
  const over = capacity.slots_available !== null && capacity.slots_available < 0;

  return (
    <Card>
      <CardContent className="flex flex-col gap-4">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <CalendarRange aria-hidden className="size-4 text-muted-foreground" />
            <h2 className="text-body-sm font-medium">{cm('slots.heading')}</h2>
          </div>
          {trigger}
        </div>
        <span data-numeric className="text-title-lg">
          {slotsValue(capacity)}
        </span>
        <SlotMeter capacity={capacity} hidePercentage />
        {over && (
          <p className="text-caption text-muted-foreground">
            <Badge variant="destructive">{cm('stats.over')}</Badge>
          </p>
        )}
      </CardContent>
    </Card>
  );
}
